import React, { ReactElement } from 'react';
import { Space, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { isEmpty } from 'lodash';

import { ContentItemBase, ContractTerm } from 'pricing/types';
import { getNameOfPricingContentFieldById } from 'pricing/utilities/localStorage/pricingContentStorage';
import {
  getTermFinalCost,
  getTermFinalPrice,
  getTermInitialCost,
  getTermInitialPrice,
  getTermMiddleCost,
  getTermMiddlePrice,
} from 'pricing/utilities/contract/contractTerms/contractTermsPricing';
import EditContractTerm from 'pricing/components/contractTerms/contractTermsTable/contractTermTableActions/EditContractTerm';
import { testId } from 'pricing/utilities/test/dataTestId';
import number from 'utilities/number/number';
import { ContractTermsTableSection } from './ContractTermsTable.styles';

interface Props {
  contractTerms: ContractTerm[] | undefined;
  withActions?: boolean;
}

function ContractTermsTable(props: Props): ReactElement | null {
  const { contractTerms, withActions } = props;

  const contractTermTableColumns: ColumnsType<ContractTerm> = [
    {
      title: 'Term ID',
      dataIndex: 'id',
      key: 'termId',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Term Type',
      dataIndex: 'breederContractTermTypeId',
      key: 'termType',
      width: 120,
      render: (value) => getNameOfPricingContentFieldById('contractTermTypes', value),
    },
    {
      title: 'Breed',
      dataIndex: 'breedId',
      key: 'breed',
      width: 120,
      render: (value) => getNameOfPricingContentFieldById('breeds', value),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 50,
    },
    {
      title: 'Colors',
      dataIndex: 'colors',
      key: 'colors',
      width: 220,
      render: (colors) =>
        colors?.map((color: ContentItemBase) => <Tag key={color.id}>{color.name}</Tag>),
    },
    {
      title: 'Varieties',
      dataIndex: 'varieties',
      key: 'varieties',
      width: 220,
      render: (varieties) =>
        varieties?.map((variety: ContentItemBase) => (
          <Tag key={variety.id}>{variety.name}</Tag>
        )),
    },
    {
      title: 'Initial Cost',
      key: 'initialCost',
      width: 90,
      render: (contractTerm) => {
        const cost = getTermInitialCost(contractTerm);
        return cost ? number.displayCurrency(cost) : undefined;
      },
    },
    {
      title: 'Initial Price',
      key: 'initialPrice',
      width: 90,
      render: (contractTerm) => {
        const price = getTermInitialPrice(contractTerm);
        return price ? number.displayCurrency(price) : undefined;
      },
    },
    {
      title: 'Middle Cost',
      key: 'middleCost',
      width: 110,
      render: (contractTerm) => {
        const cost = getTermMiddleCost(contractTerm);
        return cost ? number.displayCurrency(cost) : undefined;
      },
    },
    {
      title: 'Middle Price',
      key: 'middlePrice',
      width: 110,
      render: (contractTerm) => {
        const price = getTermMiddlePrice(contractTerm);
        return price ? number.displayCurrency(price) : undefined;
      },
    },
    {
      title: 'Final Cost',
      key: 'finalCost',
      width: 90,
      render: (contractTerm) => {
        const cost = getTermFinalCost(contractTerm);
        return cost ? number.displayCurrency(cost) : undefined;
      },
    },
    {
      title: 'Final Price',
      key: 'finalPrice',
      width: 90,
      render: (contractTerm) => {
        const price = getTermFinalPrice(contractTerm);
        return price ? number.displayCurrency(price) : undefined;
      },
    },
  ];

  const contractTermTableColumnsWithActions: ColumnsType<ContractTerm> = [
    ...contractTermTableColumns,
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right',
      render: (_, contractTerm) => {
        return (
          <Space>
            <EditContractTerm contractTerm={contractTerm} />
          </Space>
        );
      },
    },
  ];

  if (isEmpty(contractTerms) && withActions) {
    return null;
  }

  return (
    <ContractTermsTableSection className="ContractTermsTable">
      <Table
        data-testid={testId.contractTermsTable}
        size="small"
        rowKey={(contractTerm) => contractTerm.id}
        dataSource={contractTerms}
        pagination={false}
        scroll={{ x: true }}
        columns={
          withActions ? contractTermTableColumnsWithActions : contractTermTableColumns
        }
      />
    </ContractTermsTableSection>
  );
}

export default ContractTermsTable;
